'use client';

import { TOCItems, TOCProvider } from 'fumadocs-ui/components/layout/toc';
import { CalendarIcon, ListIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { MarkdownPreview } from '@/shared/blocks/common';
import { Crumb } from '@/shared/blocks/common/crumb';
import { type Post as PostType } from '@/shared/types/blocks/blog';
import { NavItem } from '@/shared/types/blocks/common';

import '@/config/style/docs.css';

export function ToolDetail({ post }: { post: PostType }) {
  const t = useTranslations('pages.tools.messages');

  const crumbItems: NavItem[] = [
    {
      title: t('crumb'),
      url: '/tools',
      icon: 'Wrench',
      is_active: false,
    },
    {
      title: post.title || '',
      url: `/tools/${post.slug}`,
      is_active: true,
    },
  ];

  const showToc = post.toc && post.toc.length > 0;

  const getMainColSpan = () => {
    if (showToc) return 'lg:col-span-9';
    return 'lg:col-span-12';
  };

  return (
    <TOCProvider toc={post.toc || []}>
      <section id={post.id}>
        <div className="py-24 md:py-32">
          <div className="mx-auto w-full max-w-7xl px-6 md:px-8">
            <Crumb items={crumbItems} />

            <div className="mt-16 text-center">
              <h1 className="text-foreground mx-auto mb-4 w-full text-3xl font-bold md:max-w-4xl md:text-4xl">
                {post.title}
              </h1>
              {post.created_at && (
                <div className="text-muted-foreground text-md mb-8 flex items-center justify-center gap-2">
                  <CalendarIcon className="size-4" /> {post.created_at}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-8 md:mt-12 lg:grid-cols-12">
              {showToc && (
                <div className="lg:col-span-3">
                  <div className="sticky top-24 hidden md:block">
                    <div className="bg-muted/30 rounded-lg p-4">
                      <h2 className="text-foreground mb-4 flex items-center gap-2 font-semibold">
                        <ListIcon className="size-4" /> {t('toc')}
                      </h2>
                      <TOCItems />
                    </div>
                  </div>
                </div>
              )}

              <div className={getMainColSpan()}>
                <article className="p-0">
                  {post.body ? (
                    <div className="docs text-foreground text-md space-y-4 font-normal *:leading-relaxed">
                      {post.body}
                    </div>
                  ) : (
                    post.content && (
                      <div className="prose prose-lg text-muted-foreground max-w-none space-y-6 *:leading-relaxed">
                        <MarkdownPreview content={post.content} />
                      </div>
                    )
                  )}
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>
    </TOCProvider>
  );
}
